'use client';

/**
 * useRouteDetailLogic - Single source of truth for the route detail (viewing) panel
 *
 * Loads the selected route and its stops, tracks visit progress, resolves the
 * starting point, keeps the map in sync (stop markers + directions) and exposes
 * the route-level actions (optimize, rename, restart, delete, remove stop).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useGetRouteDetailsQuery,
  useGetRouteStopsQuery,
  useUpdateRouteMutation,
  useDeleteRouteMutation,
  useDeleteRouteStopMutation,
  type Route,
  type RouteStopResponse,
} from 'src/api/redux';
import { captureException } from 'src/observability';
import { extractErrorMessage } from 'src/components/Strategy/Profiles/utils/errorUtils';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  setRoutesTempStops,
  setRoutesDirections,
  clearDirections,
} from 'src/redux';
import {
  clearSessionStaleStops,
  endSession,
  markVisited,
  selectRouteNavigationHydrated,
  selectRouteRoundTrip,
  selectRouteSession,
  setCurrentStop,
  type VisitedStopActivity,
} from 'src/redux/slices/routeNavigationSlice';
import useUserLocation from 'src/hooks/geolocation/useUserLocation';
import useRouteState from './useRouteState';
import useRouteActions from './useRouteActions';
import useRouteDirections from './useRouteDirections';
import useRouteOptimize from './useRouteOptimize';
import { MAX_ROUTE_STOPS } from 'src/api/redux/routes/constants';
import {
  buildDirectionsPoints,
  enrichRouteStops,
  getNextUnvisitedStop,
  getStopKey,
} from './utils';

const METERS_PER_MILE = 1609.344;

export type StopCardState = 'default' | 'nextUp' | 'active' | 'visited';

export interface RouteStartingPoint {
  lat: number;
  lng: number;
  /** True when the point is the origin saved by the last optimize */
  isPersisted: boolean;
}

export interface UseRouteDetailLogicReturn {
  route: Route | undefined;
  stops: RouteStopResponse[];
  isLoading: boolean;
  /** True when the stops request failed, so the panel can't claim the route is empty */
  isStopsError: boolean;
  /** Refetches whichever of the route and its stops failed to load */
  handleRetry: () => void;

  // Visit progress
  visitedCount: number;
  isRouteComplete: boolean;
  roundTrip: boolean;
  getStopState: (stop: RouteStopResponse) => StopCardState;
  getVisitedActivity: (stop: RouteStopResponse) => VisitedStopActivity | undefined;

  // Header stats
  startingPoint: RouteStartingPoint | null;
  /** Total driving distance in miles, null until known */
  distanceMiles: number | null;

  // Optimize
  isOptimizing: boolean;
  optimizeError: string | null;
  canOptimize: boolean;

  // Handlers
  handleOptimize: (roundtrip: boolean) => Promise<void>;
  handleRename: (label: string) => Promise<void>;
  handleRestart: () => void;
  /** Resolves null on success, or the API error message when the delete failed */
  handleDelete: () => Promise<string | null>;
  handleRemoveStop: (stop: RouteStopResponse) => Promise<void>;
  handleStartStop: (stop: RouteStopResponse) => void;
  /** Clears the active stop, e.g. after pressing Go and changing course */
  handleCancelStop: () => void;
  handleMarkVisited: (
    stop: RouteStopResponse,
    activity?: VisitedStopActivity
  ) => void;
  handleEdit: () => void;
  handleClose: () => void;

  isRenaming: boolean;
  isDeleting: boolean;
}

const useRouteDetailLogic = (): UseRouteDetailLogicReturn => {
  const dispatch = useAppDispatch();
  const { selectedRouteId } = useRouteState();
  const { startEdit, cancel } = useRouteActions();
  const routeId = selectedRouteId ? Number(selectedRouteId) : null;

  // Route data
  const {
    data: route,
    isLoading: isLoadingRoute,
    refetch: refetchRoute,
  } = useGetRouteDetailsQuery(selectedRouteId || '', { skip: !selectedRouteId });
  const {
    data: stopsData,
    isLoading: isLoadingStops,
    isError: isStopsError,
    refetch: refetchStops,
  } = useGetRouteStopsQuery(selectedRouteId || '', { skip: !selectedRouteId });
  const stops = useMemo(
    () => [...(stopsData ?? [])].sort((a, b) => a.position - b.position),
    [stopsData]
  );

  const [updateRoute, { isLoading: isRenaming }] = useUpdateRouteMutation();
  const [deleteRoute, { isLoading: isDeleting }] = useDeleteRouteMutation();
  const [deleteRouteStop] = useDeleteRouteStopMutation();
  const {
    optimize,
    isOptimizing,
    error: optimizeError,
  } = useRouteOptimize();

  // Visit progress
  const session = useAppSelector((state) => selectRouteSession(state, routeId));
  const roundTrip = useAppSelector((state) =>
    selectRouteRoundTrip(state, routeId)
  );
  // Visit progress and optimize write the persisted slice; wait for hydration
  const isNavigationHydrated = useAppSelector(selectRouteNavigationHydrated);

  const visitedStopKeys = useMemo(
    () => session?.visitedStopKeys ?? [],
    [session]
  );
  const currentStopKey = session?.currentStopKey ?? null;
  const visitedCount = stops.filter((stop) =>
    visitedStopKeys.includes(getStopKey(stop))
  ).length;
  const isRouteComplete = stops.length > 0 && visitedCount === stops.length;
  const nextUpKey = useMemo(() => {
    const next = getNextUnvisitedStop(stops, visitedStopKeys);
    return next ? getStopKey(next) : null;
  }, [stops, visitedStopKeys]);

  const getStopState = useCallback(
    (stop: RouteStopResponse): StopCardState => {
      const key = getStopKey(stop);
      if (visitedStopKeys.includes(key)) return 'visited';
      if (key === currentStopKey) return 'active';
      if (currentStopKey === null && key === nextUpKey) return 'nextUp';
      return 'default';
    },
    [visitedStopKeys, currentStopKey, nextUpKey]
  );

  const getVisitedActivity = useCallback(
    (stop: RouteStopResponse) => session?.visitedActivities?.[getStopKey(stop)],
    [session]
  );

  // Starting point: the origin saved by the last optimize wins, live location
  // is the fallback for routes that were never optimized.
  const { location: userLocation } = useUserLocation();
  const originLat = route?.origin_lat ?? null;
  const originLng = route?.origin_lng ?? null;
  const liveLat = userLocation?.lat ?? null;
  const liveLng = userLocation?.lng ?? null;
  const startingPoint = useMemo<RouteStartingPoint | null>(() => {
    if (originLat !== null && originLng !== null) {
      return { lat: originLat, lng: originLng, isPersisted: true };
    }
    if (liveLat !== null && liveLng !== null) {
      return { lat: liveLat, lng: liveLng, isPersisted: false };
    }
    return null;
  }, [originLat, originLng, liveLat, liveLng]);

  // Drop visits for stops that were removed from the route
  useEffect(() => {
    if (!isNavigationHydrated || routeId === null || !stopsData) return;
    dispatch(
      clearSessionStaleStops({
        routeId,
        validStopKeys: stopsData.map(getStopKey),
      })
    );
  }, [isNavigationHydrated, routeId, stopsData, dispatch]);

  // Map sync + total distance: stop markers come from tempStops and the path
  // from directions (starting point first, back to it for round trips).
  const { fetchDirections } = useRouteDirections();
  const [distanceMeters, setDistanceMeters] = useState<number | null>(null);

  useEffect(() => {
    dispatch(setRoutesTempStops(enrichRouteStops(stops)));
  }, [stops, dispatch]);

  useEffect(() => {
    // Drop the previous route's distance and path so the header never pairs
    // new stops with old mileage while the replacement request is in flight.
    setDistanceMeters(null);
    dispatch(clearDirections());

    const hasEnoughPoints = startingPoint
      ? stops.length >= 1
      : stops.length >= 2;
    if (!hasEnoughPoints) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const run = async () => {
      const directions = await fetchDirections(
        buildDirectionsPoints(stops, startingPoint, roundTrip),
        { signal: controller.signal }
      );
      if (cancelled) return;
      if (directions) {
        dispatch(setRoutesDirections(directions));
      } else {
        dispatch(clearDirections());
      }
      // A partly routed route's total only counts the routed sections, so the
      // header shows no mileage rather than a figure that looks complete.
      const isPartial = directions?.legs?.some((leg) => leg.isApproximate);
      setDistanceMeters(
        isPartial ? null : (directions?.totalDistanceMeters ?? null)
      );
    };

    run().catch((error) => {
      if (!cancelled) {
        setDistanceMeters(null);
        captureException(error, { tags: { context: 'route-detail-distance' } });
      }
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [stops, startingPoint, roundTrip, fetchDirections, dispatch]);

  // Handlers
  const canOptimize =
    stops.length >= 2 &&
    stops.length <= MAX_ROUTE_STOPS &&
    !isRouteComplete &&
    !isOptimizing &&
    isNavigationHydrated;

  const handleOptimize = useCallback(
    async (roundtrip: boolean) => {
      if (!selectedRouteId || !canOptimize) return;
      await optimize({ routeId: selectedRouteId, roundtrip });
    },
    [selectedRouteId, canOptimize, optimize]
  );

  const handleRename = useCallback(
    async (label: string) => {
      if (!selectedRouteId) return;
      await updateRoute({ routeId: selectedRouteId, label }).unwrap();
    },
    [selectedRouteId, updateRoute]
  );

  // Visit-progress writes wait for hydration, which would otherwise overwrite them
  const handleRestart = useCallback(() => {
    if (routeId === null || !isNavigationHydrated) return;
    dispatch(endSession(routeId));
  }, [routeId, isNavigationHydrated, dispatch]);

  const handleDelete = useCallback(async () => {
    if (!selectedRouteId || routeId === null) return null;
    try {
      await deleteRoute(selectedRouteId).unwrap();
    } catch (error) {
      // 4xx is shown to the user; 500s are already captured by the API layer
      return extractErrorMessage(error, 'Unable to delete this route.');
    }
    dispatch(endSession(routeId));
    cancel();
    return null;
  }, [selectedRouteId, routeId, deleteRoute, dispatch, cancel]);

  const handleRemoveStop = useCallback(
    async (stop: RouteStopResponse) => {
      if (!selectedRouteId) return;
      try {
        await deleteRouteStop({
          routeId: selectedRouteId,
          stopId: stop.id,
        }).unwrap();
      } catch (error) {
        captureException(error, { tags: { context: 'route-remove-stop' } });
      }
    },
    [selectedRouteId, deleteRouteStop]
  );

  const handleStartStop = useCallback(
    (stop: RouteStopResponse) => {
      if (routeId === null || !isNavigationHydrated) return;
      dispatch(setCurrentStop({ routeId, stopKey: getStopKey(stop) }));
    },
    [routeId, isNavigationHydrated, dispatch]
  );

  const handleCancelStop = useCallback(() => {
    if (routeId === null || !isNavigationHydrated) return;
    dispatch(setCurrentStop({ routeId, stopKey: null }));
  }, [routeId, isNavigationHydrated, dispatch]);

  const handleMarkVisited = useCallback(
    (stop: RouteStopResponse, activity?: VisitedStopActivity) => {
      if (routeId === null || !isNavigationHydrated) return;
      dispatch(markVisited({ routeId, stopKey: getStopKey(stop), activity }));
    },
    [routeId, isNavigationHydrated, dispatch]
  );

  const handleRetry = useCallback(() => {
    if (!selectedRouteId) return;
    refetchRoute();
    refetchStops();
  }, [selectedRouteId, refetchRoute, refetchStops]);

  const handleEdit = useCallback(() => {
    if (!selectedRouteId) return;
    startEdit({
      routeId: selectedRouteId,
      label: route?.label ?? '',
      stops: [],
    });
  }, [selectedRouteId, route, startEdit]);

  return {
    route,
    stops,
    isLoading: isLoadingRoute || isLoadingStops,
    visitedCount,
    isRouteComplete,
    roundTrip,
    getStopState,
    getVisitedActivity,
    isStopsError,
    handleRetry,
    startingPoint,
    distanceMiles:
      distanceMeters === null ? null : distanceMeters / METERS_PER_MILE,
    isOptimizing,
    optimizeError,
    canOptimize,
    handleOptimize,
    handleRename,
    handleRestart,
    handleDelete,
    handleRemoveStop,
    handleStartStop,
    handleCancelStop,
    handleMarkVisited,
    handleEdit,
    handleClose: cancel,
    isRenaming,
    isDeleting,
  };
};

export default useRouteDetailLogic;
